import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { getAnnouncementImageUrl } from '../utils/imageUrl';
import {
  DASHBOARD_BRAND_PRIMARY,
  DASHBOARD_BRAND_PRIMARY_SOFT,
} from '../theme/dashboardBrand';
import { useMobileImagePicker } from '../hooks/useMobileImagePicker';

interface ImageUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  maxImages?: number;
  required?: boolean;
  error?: string;
  /** ex. `/uploads/category-defaults/...` — aperçu si aucune photo utilisateur */
  categoryDefaultImageUrl?: string | null;
  /** Juste avant ouverture caméra/galerie (persistance brouillon Android). */
  onBeforeOpen?: () => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onImagesChange,
  maxImages = 5,
  required = false,
  error,
  onBeforeOpen,
}) => {
  const [uploading, setUploading] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleImageUpload = useCallback(async (file: File) => {
    if (images.length >= maxImages) {
      alert(`Vous ne pouvez pas ajouter plus de ${maxImages} images.`);
      return;
    }

    setUploading(true);
    try {
      const response = await apiService.uploadImage(file);
      const newImageUrl = response.data.filename;
      onImagesChange([...images, newImageUrl]);
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      alert("Erreur lors de l'upload (réseau ou photo trop lourde). Réessayez avec une photo plus légère.");
    } finally {
      setUploading(false);
    }
  }, [images, maxImages, onImagesChange]);

  const validateAndUpload = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) {
        alert('Veuillez sélectionner un fichier image.');
        return;
      }

      // Photos Samsung souvent > 5 Mo : on accepte et on compresse côté upload.
      if (file.size > 40 * 1024 * 1024) {
        alert("L'image ne doit pas dépasser 40MB.");
        return;
      }

      void handleImageUpload(file);
    },
    [handleImageUpload],
  );

  const { openPicker, hiddenInputs, sourceMenu } = useMobileImagePicker({
    onFileSelected: validateAndUpload,
    onBeforeOpen,
    disabled: uploading,
  });

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);

    setImageErrors((prev) => {
      const newErrors = new Set(prev);
      newErrors.delete(index);
      return newErrors;
    });
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        handleImageUpload(file);
      }
    }
  };

  const handleImageError = (index: number) => {
    setImageErrors((prev) => new Set(prev).add(index));
  };

  useEffect(() => {
    setImageErrors(new Set());
  }, [images]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Photo de l&apos;objet
        {required ? (
          <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
            *
          </Box>
        ) : null}
      </Typography>

      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      ) : null}

      {images.length < maxImages && (
        <Box
          ref={dropZoneRef}
          role="button"
          tabIndex={0}
          aria-label="Ajouter une photo"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker(dropZoneRef.current);
            }
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !uploading && openPicker(dropZoneRef.current)}
          sx={{
            width: '100%',
            minHeight: 140,
            border: '2px dashed',
            borderColor: error ? 'error.main' : DASHBOARD_BRAND_PRIMARY,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploading ? 'wait' : 'pointer',
            bgcolor: DASHBOARD_BRAND_PRIMARY_SOFT,
            mb: images.length > 0 ? 2 : 0,
            '&:hover': {
              borderColor: DASHBOARD_BRAND_PRIMARY,
              bgcolor: 'rgba(81, 190, 238, 0.24)',
            },
          }}
        >
          {uploading ? (
            <CircularProgress size={40} sx={{ color: DASHBOARD_BRAND_PRIMARY }} />
          ) : (
            <PhotoCameraIcon sx={{ fontSize: 48, color: DASHBOARD_BRAND_PRIMARY }} />
          )}
        </Box>
      )}

      {hiddenInputs}
      {sourceMenu}

      {images.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
          {images.map((image, index) => (
            <Card key={`image-${image}-${index}`} sx={{ width: 120, height: 120, position: 'relative' }}>
              {imageErrors.has(index) ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'grey.100',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="caption" textAlign="center">
                    Erreur
                  </Typography>
                </Box>
              ) : (
                <CardMedia
                  component="img"
                  height="120"
                  image={getAnnouncementImageUrl(image)}
                  alt={`Image ${index + 1}`}
                  sx={{ objectFit: 'cover' }}
                  onError={() => handleImageError(index)}
                />
              )}
              <IconButton
                size="small"
                onClick={() => removeImage(index)}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ImageUpload;
